import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { productsAPI, cartAPI, wishlistAPI, chatAPI, settingsAPI, handleAPIError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import Header from '../components/Header';
import MobileFooter from '../components/MobileFooter';
import ProductCard from '../components/ProductCard';
import DesktopHero from '../components/DesktopHero';
import DesktopShoghtaneh from '../components/DesktopShoghtaneh';
import DesktopHomeContent from '../components/DesktopHomeContent';
import DesktopCategoryRow from '../components/DesktopCategoryRow';
import DesktopFeatures from '../components/DesktopFeatures';
import DesktopFooter from '../components/DesktopFooter';
import LazyImage from '../components/LazyImage';
import { 
  FunnelIcon, 
  Squares2X2Icon, 
  ListBulletIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SparklesIcon,
  GiftIcon,
  HeartIcon,
  StarIcon,
  PhoneIcon,
  BookOpenIcon,
  InformationCircleIcon,
  TruckIcon,
  ShieldCheckIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

const MobileHomeUI2 = ({ 
  categories, 
  products, 
  onSelectCategory,
  bestSellers: propBestSellers = [],
  newestProducts: propNewestProducts = []
}) => {
  const navigate = useNavigate();
  const { systemSettings, resolveMediaUrl } = useSettings();
  const bannerTrackRef = useRef(null);
  const rawBanners = Array.isArray(systemSettings?.banners) ? systemSettings.banners : [];
  // Render placeholder slides only while settings are still loading; once we
  // know the store has no banners, render nothing (was: 3 empty red slabs).
  const settingsLoaded = !!systemSettings;
  const baseBanners = rawBanners.length > 0
    ? rawBanners
    : (settingsLoaded
        ? []
        : Array.from({ length: 3 }).map(() => ({ image_url: null, redirect_url: '' })));
  const bannerSlidesCount = baseBanners.length;
  const bannerVirtualItems = bannerSlidesCount > 1
    ? [baseBanners[bannerSlidesCount - 1], ...baseBanners, baseBanners[0]]
    : baseBanners;
  const [bannerVirtualIndex, setBannerVirtualIndex] = useState(bannerSlidesCount > 1 ? 1 : 0);
  const skipBannerAnimationRef = useRef(false);
  const bannerScrollLockRef = useRef(false);
  const bannerScrollTimerRef = useRef(null);
  const [shoghtanehStatus, setShoghtanehStatus] = useState(null);
  const [shoghtanehRemaining, setShoghtanehRemaining] = useState(0);
  const shoghtanehFetchLockRef = useRef(false);
  const [introductions, setIntroductions] = useState([]);
  const [premiumServices, setPremiumServices] = useState([]);

  const getBannerSlides = () => {
    const track = bannerTrackRef.current;
    if (!track) return [];
    return Array.from(track.querySelectorAll('.banner-slide'));
  };

  const activeBannerIndex = bannerSlidesCount > 1
    ? ((bannerVirtualIndex - 1 + bannerSlidesCount) % bannerSlidesCount)
    : 0;

  useEffect(() => {
    const baseAutoMs = Number.isFinite(Number(systemSettings?.banner_auto_change_ms))
      ? Math.max(500, Number(systemSettings.banner_auto_change_ms))
      : 3000;
    const autoMs = Math.round(baseAutoMs * 1.2);
    if (bannerSlidesCount <= 1) return undefined;
    const id = window.setInterval(() => {
      bannerScrollLockRef.current = true;
      setBannerVirtualIndex((prev) => prev + 1);
    }, autoMs);
    return () => window.clearInterval(id);
  }, [systemSettings?.banner_auto_change_ms, bannerSlidesCount]);

  useEffect(() => {
    const behavior = skipBannerAnimationRef.current ? 'auto' : 'smooth';
    skipBannerAnimationRef.current = false;
    {
      const track = bannerTrackRef.current;
      if (track) {
        const slides = getBannerSlides();
        const slide = slides[bannerVirtualIndex];
        if (slide) {
          const left = slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2;
          track.scrollTo({ left, behavior });
        }
      }
    }

    if (bannerSlidesCount <= 1) return;
    if (bannerVirtualIndex === 0) {
      const t = window.setTimeout(() => {
        skipBannerAnimationRef.current = true;
        setBannerVirtualIndex(bannerSlidesCount);
      }, 350);
      return () => window.clearTimeout(t);
    }
    if (bannerVirtualIndex === bannerSlidesCount + 1) {
      const t = window.setTimeout(() => {
        skipBannerAnimationRef.current = true;
        setBannerVirtualIndex(1);
      }, 350);
      return () => window.clearTimeout(t);
    }
  }, [bannerVirtualIndex, bannerSlidesCount]);

  useEffect(() => {
    return () => {
      if (bannerScrollTimerRef.current) {
        window.clearTimeout(bannerScrollTimerRef.current);
        bannerScrollTimerRef.current = null;
      }
    };
  }, []);

  const handleBannerScroll = () => {
    if (bannerScrollTimerRef.current) window.clearTimeout(bannerScrollTimerRef.current);
    bannerScrollTimerRef.current = window.setTimeout(() => {
      const track = bannerTrackRef.current;
      if (!track) return;
      const slides = getBannerSlides();
      if (!slides.length) return;
      const centerX = track.scrollLeft + track.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Infinity;
      for (let i = 0; i < slides.length; i += 1) {
        const s = slides[i];
        const sCenter = s.offsetLeft + s.clientWidth / 2;
        const d = Math.abs(sCenter - centerX);
        if (d < closestDistance) {
          closestDistance = d;
          closestIndex = i;
        }
      }

      if (bannerSlidesCount > 1) {
        if (closestIndex === 0) {
          skipBannerAnimationRef.current = true;
          bannerScrollLockRef.current = true;
          setBannerVirtualIndex(bannerSlidesCount);
          return;
        }
        if (closestIndex === bannerSlidesCount + 1) {
          skipBannerAnimationRef.current = true;
          bannerScrollLockRef.current = true;
          setBannerVirtualIndex(1);
          return;
        }
      }

      if (!bannerScrollLockRef.current) {
        setBannerVirtualIndex(closestIndex);
      } else {
        bannerScrollLockRef.current = false;
      }
    }, 120);
  };

  const ui2Categories = Array.isArray(categories)
    ? categories.filter((c) => c && typeof c === 'object' && c.slug).slice(0, 10)
    : [];

  const pickCategoryImage = (cat) => {
    const raw = cat?.image || cat?.image_url || cat?.thumbnail || cat?.icon || null;
    return resolveMediaUrl(raw);
  };

  const pickProductImage = (product) => {
    const candidates = [
      product?.main_image,
      product?.mainImage,
      product?.main_image_url,
      product?.mainImageUrl,
      product?.image,
      product?.thumbnail_url,
      product?.thumbnail,
      Array.isArray(product?.images)
        ? (typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.image)
        : null,
    ];
    for (const c of candidates) {
      const resolved = resolveMediaUrl(c);
      if (resolved) return resolved;
    }
    return null;
  };

  const toFaDigits = (value) => {
    const digits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    return String(value ?? '').replace(/\d/g, (d) => digits[Number(d)] ?? d);
  };

  const formatPrice = (price) => {
    const n = Number(price);
    if (!Number.isFinite(n)) return '';
    return `${new Intl.NumberFormat('fa-IR').format(n)} تومان`;
  };

  const getOriginalPrice = (product) => {
    const n = Number(product?.price ?? product?.originalPrice ?? product?.original_price);
    return Number.isFinite(n) ? n : null;
  };

  const getDiscountedPrice = (product) => {
    const n = Number(
      product?.discount_price ??
      product?.discountPrice ??
      product?.final_price ??
      product?.finalPrice ??
      product?.price
    );
    return Number.isFinite(n) ? n : null;
  };

  const getDiscountPercent = (product) => {
    const original = getOriginalPrice(product);
    const discounted = getDiscountedPrice(product);
    if (!Number.isFinite(original) || !Number.isFinite(discounted)) return null;
    if (original <= 0 || discounted >= original) return null;
    return Math.max(1, Math.round((1 - discounted / original) * 100));
  };

  const formatPercentFa = (percent) => {
    const n = Number(percent);
    if (!Number.isFinite(n)) return '';
    return new Intl.NumberFormat('fa-IR').format(n);
  };

  const formatCountdownFa = (secondsTotal) => {
    const total = Math.max(0, Math.floor(Number(secondsTotal) || 0));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const ascii = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return toFaDigits(ascii);
  };

  const shoghtanehPhase = shoghtanehStatus?.phase || 'disabled';
  const shoghtanehRemainingSeconds = Math.max(0, Number(shoghtanehStatus?.remaining_seconds) || 0);

  const fetchShoghtaneh = useCallback(async () => {
    if (shoghtanehFetchLockRef.current) return;
    shoghtanehFetchLockRef.current = true;
    try {
      const res = await productsAPI.getShoghtanehStatus();
      const data = res?.data || null;
      setShoghtanehStatus(data);
      setShoghtanehRemaining(Math.max(0, Number(data?.remaining_seconds) || 0));
    } catch (_) {
      setShoghtanehStatus(null);
      setShoghtanehRemaining(0);
    } finally {
      shoghtanehFetchLockRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchShoghtaneh();
    const id = window.setInterval(fetchShoghtaneh, 30000);
    return () => window.clearInterval(id);
  }, [fetchShoghtaneh]);

  useEffect(() => {
    let mounted = true;
    const loadIntroductions = async () => {
      try {
        const res = await productsAPI.getProductIntroductions();
        const items = res?.data?.results;
        if (!mounted) return;
        setIntroductions(Array.isArray(items) ? items : []);
      } catch (_) {
        if (!mounted) return;
        setIntroductions([]);
      }
    };
    loadIntroductions();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadPremiumServices = async () => {
      try {
        const resp = await settingsAPI.getPremiumServices();
        const items = resp?.data?.results;
        if (!mounted) return;
        const normalized = Array.isArray(items) ? items : [];
        normalized.sort((a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0));
        setPremiumServices(normalized);
      } catch (_) {
        if (!mounted) return;
        setPremiumServices([]);
      }
    };
    loadPremiumServices();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    setShoghtanehRemaining(shoghtanehRemainingSeconds);
  }, [shoghtanehRemainingSeconds]);

  useEffect(() => {
    if (shoghtanehPhase === 'disabled') return;
    if (shoghtanehRemaining <= 0) {
      fetchShoghtaneh();
      return;
    }
    const id = window.setInterval(() => {
      setShoghtanehRemaining((prev) => Math.max(0, (Number(prev) || 0) - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [fetchShoghtaneh, shoghtanehPhase, shoghtanehRemaining]);

  const ui2Products = Array.isArray(products) ? products : [];
  
  const bestSellers = useMemo(() => {
    if (propBestSellers && propBestSellers.length > 0) return propBestSellers;
    return [...ui2Products]
      .sort((a, b) => (Number(b?.reviewCount) || 0) - (Number(a?.reviewCount) || 0))
      .slice(0, 10);
  }, [propBestSellers, ui2Products]);

  const newest = useMemo(() => {
    if (propNewestProducts && propNewestProducts.length > 0) return propNewestProducts;
    return ui2Products.slice(0, 10);
  }, [propNewestProducts, ui2Products]);

  const showcase = ui2Products.slice(0, 4);
  const showcaseIntroductions = Array.isArray(introductions) ? introductions.slice(0, 4) : [];

  const shoghtanehProducts = Array.isArray(shoghtanehStatus?.products) ? shoghtanehStatus.products : [];
  const shoghtanehPlaceholderCount = Math.max(0, Number(shoghtanehStatus?.placeholder_count) || 0);

  return (
    <div className="home-ui2">
      <div className="app">
        <main className="main">
          <div className="page-padding">
            {baseBanners.length > 0 && (
            <section className="banner">
              <div className="banner-track" ref={bannerTrackRef} onScroll={handleBannerScroll}>
                {bannerVirtualItems.map((banner, idx) => (
                  <div className="banner-slide" key={`${banner?.id ?? idx}-${idx}`}>
                    <button
                      type="button"
                      className="banner-card"
                      onClick={() => {
                        const raw = String(banner?.redirect_url || '').trim();
                        if (!raw) return;
                        if (/^https?:\/\//i.test(raw)) {
                          window.location.assign(raw);
                          return;
                        }
                        const path = raw.startsWith('/') ? raw : `/${raw}`;
                        navigate(path);
                      }}
                    >
                      {banner?.image_url ? (
                        <LazyImage 
                          className="banner-image" 
                          src={resolveMediaUrl(banner.image_url)} 
                          alt="" 
                        />
                      ) : null}
                    </button>
                  </div>
                ))}
              </div>
              <div className="banner-dots">
                {Array.from({ length: bannerSlidesCount }).map((_, idx) => (
                  <button
                    key={idx}
                    className={['dot-btn', idx === activeBannerIndex ? 'is-active' : ''].filter(Boolean).join(' ')}
                    type="button"
                    aria-label={`slide ${idx + 1}`}
                    onClick={() => {
                      bannerScrollLockRef.current = true;
                      setBannerVirtualIndex(bannerSlidesCount > 1 ? idx + 1 : idx);
                    }}
                  >
                    <span className={['dot', idx === activeBannerIndex ? 'is-active' : ''].filter(Boolean).join(' ')} />
                  </button>
                ))}
              </div>
            </section>
            )}

            <section className="hscroll">
              <h2 className="section-title">دسته بندی</h2>
              {ui2Categories.length === 0 ? (
                <div
                  className="mx-4 mb-4 rounded-xl border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-500"
                  role="status"
                >
                  دسته‌بندی‌ای یافت نشد.
                </div>
              ) : (
              <div className="hscroll-row categories-row">
                {ui2Categories.map((cat) => (
                  <button
                    key={cat.id ?? cat.slug ?? cat.name}
                    className="hscroll-item category-item"
                    type="button"
                    onClick={() => onSelectCategory?.(cat.slug)}
                  >
                    <div className="thumb-red">
                      {pickCategoryImage(cat) ? <LazyImage src={pickCategoryImage(cat)} alt="" /> : null}
                    </div>
                    <div className="hscroll-label">{cat.name}</div>
                  </button>
                ))}
              </div>
              )}
            </section>

            {shoghtanehPhase !== 'disabled' ? (
              <section className="shoghtaneh">
                <div className="shoghtaneh-fixed">
                  <div className="shoghtaneh-header">
                    <div className="shoghtaneh-title">شگفتانه</div>
                    <img className="shoghtaneh-gift" src="/ui2/giftbox1.png" alt="Gift Box" />
                    <div className="shoghtaneh-countdown">{formatCountdownFa(shoghtanehRemaining)}</div>
                  </div>
                  {shoghtanehPhase === 'rest' && (shoghtanehStatus?.rest_message || '').trim() ? (
                    <div className="shoghtaneh-rest-message">{shoghtanehStatus.rest_message}</div>
                  ) : null}
                </div>
                <div className="shoghtaneh-row">
                  {(shoghtanehPhase === 'active' || shoghtanehPhase === 'reserve') && shoghtanehProducts.length > 0
                    ? shoghtanehProducts.slice(0, 10).map((p) => (
                        <button
                          key={p.id}
                          className="shoghtaneh-item"
                          type="button"
                          onClick={() => navigate(`/product/${p.id}`)}
                        >
                          <div className="thumb-white">
                            {pickProductImage(p) ? <LazyImage src={pickProductImage(p)} alt="" /> : null}
                          </div>
                          <div className="shoghtaneh-label">{p.title}</div>
                          <div className="shoghtaneh-prices">
                            {getDiscountedPrice(p) !== null && getOriginalPrice(p) !== null && getDiscountedPrice(p) < getOriginalPrice(p) ? (
                              <div className="shoghtaneh-original">{formatPrice(getOriginalPrice(p))}</div>
                            ) : null}
                            {getDiscountPercent(p) ? (
                              <div className="shoghtaneh-discount" dir="ltr">-{formatPercentFa(getDiscountPercent(p))}%</div>
                            ) : null}
                            {getDiscountedPrice(p) !== null ? (
                              <div className="shoghtaneh-final">{formatPrice(getDiscountedPrice(p))}</div>
                            ) : null}
                          </div>
                        </button>
                      ))
                    : Array.from({ length: Math.max(1, shoghtanehPlaceholderCount || 2) }).map((_, idx) => (
                        <div key={`shoghtaneh-placeholder-${idx}`} className="shoghtaneh-item shoghtaneh-item--placeholder">
                          <div className="thumb-white">
                            <div className="shoghtaneh-placeholder-icon">؟!</div>
                          </div>
                          <div className="shoghtaneh-label">به زودی</div>
                        </div>
                      ))}
                </div>
              </section>
            ) : null}

            <section className="hscroll">
              <h2 className="section-title">پر فروش ترین ها</h2>
              {bestSellers.length === 0 ? (
                <div
                  className="mx-4 mb-4 rounded-xl border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-500"
                  role="status"
                >
                  هنوز محصولی برای نمایش موجود نیست.
                </div>
              ) : (
              <div className="hscroll-row products-row">
                {bestSellers.map((p) => (
                  <button
                    key={p.id}
                    className="hscroll-item product-item"
                    type="button"
                    onClick={() => navigate(`/product/${p.id}`)}
                  >
                    <div className="thumb-red">
                      <LazyImage src={pickProductImage(p)} alt="" />
                    </div>
                    <div className="hscroll-label">{p.title}</div>
                    {formatPrice(p.price) ? <div className="hscroll-price">{formatPrice(p.price)}</div> : null}
                  </button>
                ))}
              </div>
              )}
            </section>

            <section className="hscroll">
              <h2 className="section-title">جدید ترین ها</h2>
              {newest.length === 0 ? (
                <div
                  className="mx-4 mb-4 rounded-xl border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-500"
                  role="status"
                >
                  هنوز محصولی برای نمایش موجود نیست.
                </div>
              ) : (
              <div className="hscroll-row products-row">
                {newest.map((p) => (
                  <button
                    key={p.id}
                    className="hscroll-item product-item"
                    type="button"
                    onClick={() => navigate(`/product/${p.id}`)}
                  >
                    <div className="thumb-red">
                      <LazyImage src={pickProductImage(p)} alt="" />
                    </div>
                    <div className="hscroll-label">{p.title}</div>
                    {formatPrice(p.price) ? <div className="hscroll-price">{formatPrice(p.price)}</div> : null}
                  </button>
                ))}
              </div>
              )}
            </section>

            <section className="showcase">
              <h2 className="section-title">معرفی محصولات</h2>
              <div className="showcase-grid">
                {(showcaseIntroductions.length > 0 ? showcaseIntroductions : showcase).map((item) => (
                  <button
                    key={item.id ?? item.slug}
                    className="showcase-item"
                    type="button"
                    onClick={() => {
                      if (showcaseIntroductions.length > 0 && item?.slug) {
                        navigate(`/introductions/${item.slug}`);
                        return;
                      }
                      if (item?.id) navigate(`/product/${item.id}`);
                    }}
                  >
                    <div className="showcase-image">
                  <LazyImage
                    src={showcaseIntroductions.length > 0 ? resolveMediaUrl(item?.main_image) : pickProductImage(item)}
                    alt={item?.title}
                    className="w-full h-full object-cover rounded-xl"
                  />
                </div>
                <div className="showcase-label">{item?.title}</div>
                  </button>
                ))}
              </div>
            </section>

            <section className="body-footer">
              <div className="body-footer-row quick-links-row">
                <Link className="clickable" to="/contact">
                  <div className="main-icon">
                    <PhoneIcon className="h-7 w-7" />
                  </div>
                  <div className="main-text">ارتباط با ما</div>
                </Link>
                {systemSettings?.contact_rubika_url ? (
                  <a
                    className="social-icon"
                    href={systemSettings.contact_rubika_url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Rubika"
                  >
                    <img className="rubika" src="/ui2/rubika.jpg" alt="روبیکا" />
                  </a>
                ) : null}
                {systemSettings?.contact_instagram_url ? (
                  <a
                    className="social-icon is-ig"
                    href={systemSettings.contact_instagram_url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Instagram"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Z"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path d="M17.5 6.5h.01" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                  </a>
                ) : null}
                {/* موبایل/تبلت: لینک «قوانین» */}
                <Link className="clickable lg:hidden" to="/terms">
                  <div className="main-icon">
                    <BookOpenIcon className="h-7 w-7" />
                  </div>
                  <div className="main-text">قوانین</div>
                </Link>
                {/* دسکتاپ: «راهنمای خرید» */}
                <a className="clickable hidden lg:flex" href="#guide">
                  <div className="main-icon">
                    <BookOpenIcon className="h-7 w-7" />
                  </div>
                  <div className="main-text">راهنمای خرید</div>
                </a>
                <Link className="clickable" to="/about">
                  <div className="main-icon">
                    <InformationCircleIcon className="h-7 w-7" />
                  </div>
                  <div className="main-text">درباره ما</div>
                </Link>
                <a className="clickable" href="#enamad" target="_blank" rel="noreferrer">
                  <img className="enamad" src="/ui2/enamad.png" alt="نماد اعتماد الکترونیکی" />
                </a>
              </div>

              <div className="mt-4 pt-4 border-t border-black/5">
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {(() => {
                    const fallback = [
                      { title: 'ارسال رایگان', text: 'برای خریدهای بالای ۵۰۰ هزار تومان', iconEl: <TruckIcon className="h-6 w-6 text-kadolar-600" /> },
                      { title: 'ضمانت اصالت', text: '۱۰۰٪ اصل و معتبر', iconEl: <ShieldCheckIcon className="h-6 w-6 text-kadolar-600" /> },
                      { title: 'بسته‌بندی لوکس', text: 'مناسب برای هدیه', iconEl: <GiftIcon className="h-6 w-6 text-kadolar-600" /> },
                      { title: 'پشتیبانی ۲۴/۷', text: 'همیشه در کنار شما', iconEl: <ChatBubbleLeftRightIcon className="h-6 w-6 text-kadolar-600" /> },
                    ];
                    const items = Array.isArray(premiumServices) && premiumServices.length > 0 ? premiumServices : fallback;
                    return items.map((item, idx) => (
                      <div
                        key={item?.id || `${item?.title || 'service'}-${idx}`}
                        className="flex items-center bg-white/70 backdrop-blur-sm p-3 lg:p-4 rounded-xl lg:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                      >
                        <div className="bg-kadolar-100 p-2 lg:p-3 rounded-xl lg:rounded-2xl ml-3 lg:ml-4">
                          {item?.icon_url ? (
                            <img src={item.icon_url} alt={item?.title || ''} className="h-5 w-5 lg:h-6 lg:w-6 object-contain" />
                          ) : (
                            item?.iconEl || null
                          )}
                        </div>
                        <div>
                          <span className="font-bold text-kadolar-800 block text-sm lg:text-base">{item?.title || '—'}</span>
                          <span className="text-kadolar-600 text-xs lg:text-sm">{item?.text || '—'}</span>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

const Home = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [wishlist, setWishlist] = useState([]);
  const [cartItemsCount, setCartItemsCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [bestSellers, setBestSellers] = useState([]);
  const [newestProducts, setNewestProducts] = useState([]);
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.matchMedia('(max-width: 1023px)').matches;
    } catch (_) {
      return false;
    }
  });

  const { isAuthenticated } = useAuth();
  const { resolveMediaUrl } = useSettings();
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let media;
    try {
      media = window.matchMedia('(max-width: 1023px)');
    } catch (_) {
      return;
    }
    const handler = () => setIsMobileViewport(media.matches);
    handler();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', handler);
      return () => media.removeEventListener('change', handler);
    }
    media.addListener(handler);
    return () => media.removeListener(handler);
  }, []);

  // Fetch initial data
  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchBestSellers();
    fetchNewestProducts();
    if (isAuthenticated) {
      fetchWishlist();
      fetchCartCount();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedCategory, sortBy, isAuthenticated]);

  const fetchBestSellers = async () => {
    try {
      // Use ordering parameter for DRF OrderingFilter
      const response = await productsAPI.getProducts({ ordering: '-sales_count', page_size: 10 });
      setBestSellers(response.data.results || []);
    } catch (error) {
      console.error('Error fetching best sellers:', error);
      // Fallback: use first 10 products if sort fails, or empty array
      // Ideally we would want to use mock data or handle gracefully
    }
  };

  const fetchNewestProducts = async () => {
    try {
      // Use ordering parameter for DRF OrderingFilter
      const response = await productsAPI.getProducts({ ordering: '-created_at', page_size: 10 });
      setNewestProducts(response.data.results || []);
    } catch (error) {
      console.error('Error fetching newest products:', error);
    }
  };

  // Fetch products
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const orderingMap = {
        'newest': '-created_at',
        'oldest': 'created_at',
        'price_low': 'price',
        'price_high': '-price',
        'popular': '-views_count',
        'best_selling': '-sales_count'
      };

      const params = {
        page: currentPage,
        category: selectedCategory,
        ordering: orderingMap[sortBy] || '-created_at'
      };
      
      const response = await productsAPI.getProducts(params);
      setProducts(response.data.results || []);
      setTotalPages(Math.ceil(response.data.count / 10) || 1);
    } catch (error) {
      const silent =
        process.env.NODE_ENV !== 'production' &&
        (error?.type === 'network_error' ||
          error?.code === 'ERR_CANCELED' ||
          error?.name === 'CanceledError' ||
          error?.message === 'backend_cooldown');
      if (!silent) console.error('Error fetching products:', error);
      // P1 review fix: در خطا داده‌ی جعلی رندر نمی‌شود — لیست خالی + پیام
      // خطا (کاربر محصول غیرواقعی نمی‌بیند).
      setProducts([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await productsAPI.getCategories();
      
      // Handle different response formats
      let categoriesData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          categoriesData = response.data;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          categoriesData = response.data.results;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          categoriesData = response.data.data;
        }
      }
      
      // Additional check to ensure categories have required properties
      const validCategories = categoriesData.filter(cat => 
        cat && typeof cat === 'object' && cat.name && typeof cat.name === 'string'
      );
      
      // Ensure we always have an array
      const allCategoriesOption = { id: 0, name: 'همه محصولات', slug: '' };
      setCategories([allCategoriesOption, ...(Array.isArray(validCategories) ? validCategories : [])]);
    } catch (error) {
      const silent =
        process.env.NODE_ENV !== 'production' &&
        (error?.type === 'network_error' ||
          error?.code === 'ERR_CANCELED' ||
          error?.name === 'CanceledError' ||
          error?.message === 'backend_cooldown');
      if (!silent) console.error('Error fetching categories:', error);
      setCategories([{ id: 0, name: 'همه محصولات', slug: '' }]);
    }
  };

  // Fetch wishlist
  const fetchWishlist = async () => {
    try {
      const response = await wishlistAPI.getWishlist();
      // Handle different response structures
      let items = [];
      if (Array.isArray(response.data)) {
        items = response.data;
      } else if (response.data && Array.isArray(response.data.items)) {
        items = response.data.items;
      } else if (response.data && Array.isArray(response.data.results)) {
        items = response.data.results;
      }
      
      setWishlist(items.map(item => 
        item.product_id || item.product?.id || item.id
      ).filter(Boolean) || []);
    } catch (error) {
      const silent =
        process.env.NODE_ENV !== 'production' &&
        (error?.type === 'network_error' ||
          error?.code === 'ERR_CANCELED' ||
          error?.name === 'CanceledError' ||
          error?.message === 'backend_cooldown');
      if (!silent) console.error('Error fetching wishlist:', error);
      setWishlist([]); // در صورت خطا، آرایه خالی تنظیم کن
    }
  };

  // Fetch cart count
  const fetchCartCount = async () => {
    try {
      const response = await cartAPI.getCart();
      const count = response.data.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
      setCartItemsCount(count);
    } catch (error) {
      const silent =
        process.env.NODE_ENV !== 'production' &&
        (error?.type === 'network_error' ||
          error?.code === 'ERR_CANCELED' ||
          error?.name === 'CanceledError' ||
          error?.message === 'backend_cooldown');
      if (!silent) console.error('Error fetching cart:', error);
    }
  };

  // Handle add to cart
  const handleAddToCart = async (productId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const p = Array.isArray(products) ? products.find((x) => Number(x?.id) === Number(productId)) : null;
    if (p?.has_custom_features || p?.hasCustomFeatures) {
      alert('لطفا وارد صفحه محصول شوید و پس از انتخاب ویژگی‌ها، آن را به سبد خرید اضافه کنید.');
      navigate(`/product/${productId}`);
      return;
    }

    try {
      await cartAPI.addToCart(productId, 1);
      setCartItemsCount(prev => prev + 1);
    } catch (error) {
      const apiError = handleAPIError(error);
      alert(apiError.message);
      console.error('Add to Cart Error:', apiError);
    }
  };

  // Handle wishlist toggle
  const handleToggleWishlist = async (productId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      if (wishlist.includes(productId)) {
        await wishlistAPI.removeFromWishlist(productId);
        setWishlist(prev => prev.filter(id => id !== productId));
      } else {
        await wishlistAPI.addToWishlist(productId);
        setWishlist(prev => [...prev, productId]);
      }
    } catch (error) {
      const apiError = handleAPIError(error);
      alert(apiError.message);
      console.error('Toggle Wishlist Error:', apiError);
    }
  };

  // Handle start chat for custom products
  const handleStartChat = async (productId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      const response = await chatAPI.startCustomProductChat(productId);
      // بک‌اند id اتاق را برمی‌گرداند؛ برای ناوبری صحیح از id استفاده می‌کنیم
      navigate(`/chat/${response.data.id}`);
    } catch (error) {
      const apiError = handleAPIError(error);
      alert(apiError.message);
      console.error('Start Chat Error:', apiError);
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Mock data for development

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-kadolar-50">
      <Header cartItemsCount={cartItemsCount} />
      
      {isMobileViewport ? (
        <MobileHomeUI2
          categories={categories}
          products={products}
          onSelectCategory={(slug) => {
            if (!slug) {
              setSelectedCategory('');
              setCurrentPage(1);
              return;
            }
            navigate(`/category/${slug}`);
          }}
          bestSellers={bestSellers}
          newestProducts={newestProducts}
        />
      ) : (
        <>
      <DesktopHero />
      {/* Categories row placed right after banners for desktop */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 relative z-20">
         <DesktopCategoryRow categories={categories} />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 relative z-20">
         <DesktopShoghtaneh />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Premium Features Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          <div className="text-center p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group">
            <div className="w-16 h-16 bg-gradient-to-br from-kadolar-500 to-kadolar-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
              <GiftIcon className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-center text-xl font-bold text-gray-900 mb-2">هدایای منحصر به فرد</h3>
            <p className="text-center text-gray-600">مجموعه‌ای دست‌چین از بهترین محصولات فرهنگی و هنری</p>
          </div>
          
          <div className="text-center p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
              <SparklesIcon className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-center text-xl font-bold text-gray-900 mb-2">کیفیت لوکس</h3>
            <p className="text-center text-gray-600">تمام محصولات با بالاترین استانداردهای کیفی تولید می‌شوند</p>
          </div>
          
          <div className="text-center p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group">
            <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
              <HeartIcon className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-center text-xl font-bold text-gray-900 mb-2">ساخت سفارشی</h3>
            <p className="text-center text-gray-600">امکان سفارش محصولات شخصی‌سازی شده مطابق سلیقه شما</p>
          </div>
        </div>

        <DesktopHomeContent 
            products={products}
            categories={categories}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            sortBy={sortBy}
            setSortBy={setSortBy}
            viewMode={viewMode}
            setViewMode={setViewMode}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            totalPages={totalPages}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            loading={loading}
            handleAddToCart={handleAddToCart}
            handleToggleWishlist={handleToggleWishlist}
            handleStartChat={handleStartChat}
            wishlist={wishlist}
            bestSellers={bestSellers}
            newestProducts={newestProducts}
            showCategories={false}
          />
      </div>
        </>
      )}

      {/* Luxury Footer */}
      <DesktopFooter />
      <MobileFooter />
    </div>
  );
};

export default Home;
